import { area as turfArea, union as turfUnion } from '@turf/turf';
import { resolveStatusFromThresholds } from './dashboardChips';

// Utilitário interno para converter valor numérico de área
const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const safeParseGeoJson = (value) => {
  if (!value) return null;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch (_e) {
      return null;
    }
  }
  if (typeof value === 'object') return value;
  return null;
};

const sumAreasFromArray = (array, getValueFn) => {
  if (!Array.isArray(array)) return 0;
  let total = 0;
  array.forEach((item) => {
    const v = getValueFn(item);
    if (v !== null && v !== undefined && Number.isFinite(v)) {
      total += v;
    }
  });
  return total;
};

const computeCarAreaHa = (properties) => {
  const carArray = Array.isArray(properties.car) ? properties.car : [];
  if (carArray.length === 0) return 0;

  const total = sumAreasFromArray(carArray, (carItem) => {
    const details = carItem?.details || {};
    const v = toNumberOrNull(details.area_da_propriedade);
    return v && v > 0 ? v : 0;
  });

  return total; // assumindo que já vem em hectares
};

const computeCafirAreaHa = (properties) => {
  const cafirArray = Array.isArray(properties.cafir) ? properties.cafir : [];
  if (cafirArray.length === 0) return 0;

  const total = sumAreasFromArray(cafirArray, (cafirItem) => {
    const v = toNumberOrNull(cafirItem?.['área_da_propriedade']);
    return v && v > 0 ? v : 0;
  });

  return total; // assumindo que já vem em hectares
};

const computeCcirAreaHa = (properties) => {
  const ccirRaw = properties.serpro_ccir;
  const ccirArray = Array.isArray(ccirRaw) ? ccirRaw : ccirRaw ? [ccirRaw] : [];

  if (ccirArray.length === 0) return 0;

  const total = sumAreasFromArray(ccirArray, (ccirItem) => {
    const v = toNumberOrNull(ccirItem?.area_total);
    return v && v > 0 ? v : 0;
  });

  return total; // assumindo que já vem em hectares
};

const collectSigefParcelsFeatures = (properties) => {
  const sigefRaw = properties.sigef;
  const sigefArray = Array.isArray(sigefRaw) ? sigefRaw : sigefRaw ? [sigefRaw] : [];

  const features = [];

  sigefArray.forEach((sigefItem) => {
    const parcels = Array.isArray(sigefItem?.parcels) ? sigefItem.parcels : [];
    parcels.forEach((p) => {
      const parsed = safeParseGeoJson(p?.geojson);
      if (!parsed || !parsed.type) return;

      if (parsed.type === 'Feature') {
        features.push(parsed);
      } else if (parsed.type === 'FeatureCollection' && Array.isArray(parsed.features)) {
        parsed.features.forEach((f) => {
          if (f && f.type === 'Feature') {
            features.push(f);
          }
        });
      } else {
        features.push({ type: 'Feature', geometry: parsed, properties: {} });
      }
    });
  });

  return features;
};

const computeSigefAreaHa = (properties) => {
  const features = collectSigefParcelsFeatures(properties);
  if (features.length === 0) return 0;

  let unionGeom = null;

  try {
    features.forEach((feat) => {
      if (!feat) return;
      if (!unionGeom) {
        unionGeom = feat;
      } else {
        const next = turfUnion(unionGeom, feat);
        if (next) {
          unionGeom = next;
        }
      }
    });
  } catch (_e) {
    // Em caso de geometria inválida, faz fallback para soma simples das áreas
    let sumM2 = 0;
    features.forEach((feat) => {
      try {
        const a = turfArea(feat);
        if (Number.isFinite(a) && a > 0) {
          sumM2 += a;
        }
      } catch (_err) {
        // ignora geometrias inválidas
      }
    });
    return sumM2 > 0 ? sumM2 / 10000 : 0;
  }

  if (!unionGeom) return 0;

  const areaM2 = turfArea(unionGeom);
  if (!Number.isFinite(areaM2) || areaM2 <= 0) return 0;

  const areaHa = areaM2 / 10000;
  return areaHa;
};

export function getAreasRegistradasDashboardData(data, dashboardChipsConfig) {
  const properties = data?.properties || {};

  const areaCar = computeCarAreaHa(properties);
  const areaSigef = computeSigefAreaHa(properties);
  const areaCafir = computeCafirAreaHa(properties);
  const areaCcir = computeCcirAreaHa(properties);

  // Seleciona base: CCIR -> SIGEF -> CAFIR -> CAR
  let baseFonte = null;
  let baseArea = null;

  if (areaCcir > 0) {
    baseFonte = 'CCIR';
    baseArea = areaCcir;
  } else if (areaSigef > 0) {
    baseFonte = 'SIGEF';
    baseArea = areaSigef;
  } else if (areaCafir > 0) {
    baseFonte = 'CAFIR';
    baseArea = areaCafir;
  } else if (areaCar > 0) {
    baseFonte = 'CAR';
    baseArea = areaCar;
  }

  const classifyDelta = (areaFonte) => {
    if (!baseArea || baseArea <= 0 || areaFonte === null || areaFonte === undefined) {
      return { deltaPercent: null, status: 'default' };
    }

    // Delta percentual assinado em relação à base
    const delta = ((areaFonte - baseArea) / baseArea) * 100;

    // Classificação padrão hard-coded
    let status;
    if (delta < -1) {
      status = 'error';
    } else if (delta > 1) {
      status = 'warning';
    } else {
      status = 'success';
    }

    // Aplicar thresholds configuráveis, se existirem
    const chipsConfig = dashboardChipsConfig || {};
    const areasCfg = chipsConfig.areasRegistradas || {};
    const deltaCfg = areasCfg.delta;

    if (deltaCfg && Array.isArray(deltaCfg.thresholds)) {
      status = resolveStatusFromThresholds(deltaCfg, delta, status) || status;
    }

    return { deltaPercent: delta, status };
  };

  const carStatus = classifyDelta(areaCar);
  const sigefStatus = classifyDelta(areaSigef);
  const cafirStatus = classifyDelta(areaCafir);

  return {
    base: {
      fonte: baseFonte,
      areaHa: baseArea,
    },
    car: {
      label: 'CAR',
      areaHa: areaCar,
      deltaPercent: carStatus.deltaPercent,
      status: carStatus.status,
    },
    sigef: {
      label: 'SIGEF',
      areaHa: areaSigef,
      deltaPercent: sigefStatus.deltaPercent,
      status: sigefStatus.status,
    },
    cafir: {
      label: 'CAFIR',
      areaHa: areaCafir,
      deltaPercent: cafirStatus.deltaPercent,
      status: cafirStatus.status,
    },
    ccir: {
      label: 'CCIR',
      areaHa: areaCcir,
      // CCIR é a base quando disponível; não faz sentido calcular delta contra si mesmo aqui
      deltaPercent: null,
      status: baseFonte === 'CCIR' ? 'success' : 'default',
    },
  };
}
